/**
 * Video Reviews — 3D coverflow video carousel (Savor-native, no Swiper).
 * Each card is either an uploaded Shopify Files video (<video>) or an external YouTube/Vimeo URL
 * (an iframe injected on play). The centered card is full-size and (optionally) autoplays muted;
 * neighbours are pushed back, tilted, scaled and dimmed. Browse with arrows, by clicking a side
 * card, or by swiping (works on mobile). Reduced motion: no autoplay, instant positioning.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class VideoReviewsComponent extends Component {
  requiredRefs = ['track'];
  #active = 0;
  #slides = [];
  #reduced = false;
  #swiped = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reduced = prefersReducedMotion();
    this.#slides = Array.from(this.querySelectorAll('.vr__slide'));
    this.#active = Math.floor((this.#slides.length - 1) / 2);

    const reveals = this.querySelectorAll('.reveal');
    if (this.#reduced) reveals.forEach((el) => el.classList.add('is-visible'));
    else {
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => io.observe(el));
    }

    this.#layout();
    this.#activate();
    window.addEventListener('resize', this.#onResize);
    this.#bindSwipe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#onResize);
  }

  /* ---- navigation (declarative on:click) ---- */
  prev() { this.#go(this.#active - 1); }
  next() { this.#go(this.#active + 1); }

  onCardClick(event) {
    if (this.#swiped) { this.#swiped = false; return; }   // ignore the click that ends a swipe
    const slide = event.target.closest('.vr__slide');
    if (!slide) return;
    const idx = this.#slides.indexOf(slide);
    if (idx === -1) return;
    if (idx !== this.#active) this.#go(idx);
    else this.#toggleActive();
  }

  #go(index) {
    this.#active = Math.max(0, Math.min(this.#slides.length - 1, index));
    this.#layout();
    this.#activate();
  }

  #layout() {
    const sample = this.#slides[0];
    const cardW = sample ? sample.offsetWidth : 240;
    const spacing = cardW * 0.62, depth = 200, angle = 32;
    this.#slides.forEach((slide, idx) => {
      const o = idx - this.#active, a = Math.abs(o);
      const scale = Math.max(0.55, 1 - a * 0.12);
      if (this.#reduced) slide.style.transition = 'none';
      slide.style.transform =
        `translate(-50%, -50%) translateX(${o * spacing}px) translateZ(${-a * depth}px) rotateY(${-o * angle}deg) scale(${scale})`;
      slide.style.zIndex = String(100 - a);
      slide.style.opacity = a > 3 ? '0' : '1';
      slide.style.pointerEvents = a > 3 ? 'none' : 'auto';
      slide.classList.toggle('is-active', o === 0);
      slide.setAttribute('aria-hidden', o === 0 ? 'false' : 'true');
    });
  }

  /** Make the active card play; stop/reset every other card. */
  #activate() {
    this.#slides.forEach((slide, idx) => {
      const isActive = idx === this.#active;
      const video = slide.querySelector('video');
      if (isActive) {
        if (this.dataset.autoplay === 'true' && !this.#reduced) this.#play(slide, { muted: true });
      } else {
        if (video) { video.pause(); try { video.currentTime = 0; } catch (e) {} }
        this.#clearEmbed(slide);
        slide.classList.remove('is-playing');
      }
    });
  }

  #toggleActive() {
    const slide = this.#slides[this.#active];
    if (!slide) return;
    const video = slide.querySelector('video');
    if (video) {
      if (video.paused) this.#play(slide, { muted: false }); else { video.pause(); slide.classList.remove('is-playing'); }
    } else if (slide.dataset.embedId) {
      if (slide.querySelector('iframe')) this.#clearEmbed(slide); else this.#play(slide, { muted: false });
    }
  }

  #play(slide, { muted }) {
    const video = slide.querySelector('video');
    if (video) {
      video.muted = muted;
      video.play().then(() => slide.classList.add('is-playing')).catch(() => {});
      return;
    }
    if (slide.dataset.embedId) this.#injectEmbed(slide, { muted });
  }

  #injectEmbed(slide, { muted }) {
    const host = slide.querySelector('.vr__embed-host');
    if (!host || host.querySelector('iframe')) return;
    const type = slide.dataset.embedType, id = slide.dataset.embedId;
    const m = muted ? 1 : 0;
    let src = '';
    if (type === 'youtube') src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=${m}&loop=1&playlist=${id}&playsinline=1&rel=0`;
    else if (type === 'vimeo') src = `https://player.vimeo.com/video/${id}?autoplay=1&muted=${m}&loop=1&playsinline=1`;
    if (!src) return;
    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
    iframe.setAttribute('allowfullscreen', '');
    iframe.loading = 'lazy';
    host.appendChild(iframe);
    slide.classList.add('is-playing');
  }

  #clearEmbed(slide) {
    const host = slide.querySelector('.vr__embed-host');
    if (host) host.innerHTML = '';
  }

  #onResize = () => this.#layout();

  /**
   * Swipe handling. Bound on the component in the CAPTURE phase so it works even over child
   * media/iframes. touch-action: pan-y (CSS) keeps vertical page scroll working.
   */
  #bindSwipe() {
    let x0 = null, y0 = null, dragging = false;
    this.addEventListener('pointerdown', (e) => {
      x0 = e.clientX; y0 = e.clientY; dragging = true; this.#swiped = false;
    }, true);
    this.addEventListener('pointermove', (e) => {
      if (!dragging || x0 === null) return;
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
        this.#swiped = true;
        if (e.cancelable) e.preventDefault();   // stop horizontal scroll/selection
      }
    }, { capture: true, passive: false });
    const end = (e) => {
      if (!dragging || x0 === null) { dragging = false; return; }
      const dx = e.clientX - x0;
      dragging = false; x0 = null;
      if (Math.abs(dx) > 40) (dx < 0 ? this.next() : this.prev());
    };
    this.addEventListener('pointerup', end, true);
    this.addEventListener('pointercancel', () => { dragging = false; x0 = null; }, true);
  }
}

if (!customElements.get('video-reviews-component')) {
  customElements.define('video-reviews-component', VideoReviewsComponent);
}
